import { useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, Upload, X, Building2, MapPin, Landmark, ImageIcon, TrendingUp, FileBarChart, LayoutDashboard, Percent, RotateCcw, BarChart2, ShieldCheck, Clock, Calculator, Target, ChevronDown, Check, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyDetails } from '@/hooks/use-company-details';
import { compressLogoImage } from '@/lib/compress-image';
import {
  uploadCompanyLogo,
  removeCompanyLogo,
  getCashBookAccounts,
  type CashBookAccountOption,
} from '@/services/companyService';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { useDataMode } from '@/contexts/DataModeContext';
import { apiFetch } from '@/lib/api-error-handler';
import { getApiBaseUrl } from '@/lib/data-service';
import { useAuth } from '@/providers/AuthProvider';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import { useFinanceSettings } from '@/hooks/use-finance-settings';
import { useUtilisationFormula } from '@/hooks/use-utilisation-formula';
import { useWizardMode } from '@/contexts/WizardModeContext';
import type { UIFormulaType } from '@/hooks/use-utilisation-formula';
import {
  GP_METHOD_LABELS,
  GP_METHOD_DESCRIPTIONS,
  type GpCalculationMethod,
} from '@/lib/gp-calculation';
import {
  type RevenueDisplayMode,
  DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT,
  DEFAULT_TARGET_UTILISATION_PERCENT,
  DEFAULT_CONCENTRATION_THRESHOLD_PERCENT,
} from '@/lib/finance-settings-storage';
import { useEacSettings } from '@/contexts/EacSettingsContext';
import type { EacMethod } from '@/lib/utils';
import type { BankDetails } from '@/types/company';

const GP_METHODS: GpCalculationMethod[] = ['simple', 'contribution', 'fully-loaded'];

const REVENUE_DISPLAY_MODES: {
  value: RevenueDisplayMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'both',
    label: 'Both (Invoiced & Recognised)',
    description:
      'Show both Invoiced Revenue and Recognised Revenue rows in the P&L Report. Recommended for fixed-price projects with formal revenue recognition.',
  },
  {
    value: 'invoiced',
    label: 'Invoiced Revenue only',
    description:
      'Show only what has been billed to the client (actualSales). Keeps the P&L clean for T&M businesses that bill time as incurred.',
  },
  {
    value: 'recognised',
    label: 'Recognised Revenue only',
    description:
      'Show only the recognised revenue figure from the Revenue Recognition schedule. Invoiced Revenue is hidden. Recommended for businesses with formal IFRS 15 / ASC 606 recognition.',
  },
];

// ── EAC Method options ────────────────────────────────────────────────────────
const EAC_METHOD_OPTIONS: { value: EacMethod; label: string; description: string }[] = [
  {
    value: 'simple',
    label: 'Simple',
    description: 'EAC = Actual Costs ÷ Completion %',
  },
  {
    value: 'performance-based',
    label: 'Performance-Based (CPI)',
    description: 'EAC = Budget × (Actual ÷ Earned Value)',
  },
  {
    value: 're-estimate',
    label: 'Re-estimate',
    description: 'PM manually enters Estimate to Complete',
  },
];

// ============================================================================
// Accordion Section — matches the Notifications page card style
// ============================================================================

interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({ icon, title, description, defaultOpen = true, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group"
        aria-expanded={open}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-6 py-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Section ref handle — exposed via forwardRef so the page can call save()
// ============================================================================

export interface SectionHandle {
  save: () => Promise<void>;
  discard?: () => void;
}
// ============================================================================
// Company Details Section
// ============================================================================

export interface CompanyImportData {
  name?: string;
  companyRegistrationNumber?: string;
  vatNumber?: string;
  phone?: string;
  email?: string;
  website?: string;
  registeredAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    country?: string;
  };
  bankDetails?: {
    bankName?: string;
    accountName?: string;
    sortCode?: string;
    accountNumber?: string;
    iban?: string;
    swiftBic?: string;
  };
  /** Cash book (bank) accounts available in the connected finance system */
  bankAccounts?: CashBookAccountOption[];
  selectedBankAccountKey?: number | null;
  selectedBankAccountCode?: string | null;
}

/** Bank account picker state, reported to hosts such as the onboarding wizard. */
export interface BankAccountSelectionState {
  accountCount: number;
  hasSelection: boolean;
}

function accountOptionValue(account: CashBookAccountOption): string {
  return account.accountKey != null ? `key:${account.accountKey}` : `code:${account.accountCode ?? ''}`;
}

interface CompanyDetailsSectionProps {
  importData?: CompanyImportData | null;
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * When true, the company information, registered address and bank details
   * fields are always read-only (sourced from Financials), regardless of the
   * live finance-integration status. The logo remains editable. Used by the
   * onboarding wizard where these details must come from Financials.
   */
  readOnlyDetails?: boolean;
  /** Notified whenever the bank account options or the current selection change. */
  onBankAccountSelectionChange?: (state: BankAccountSelectionState) => void;
}

export const CompanyDetailsSection = forwardRef(
  function CompanyDetailsSection({ importData, onDirtyChange, readOnlyDetails = false, onBankAccountSelectionChange }: CompanyDetailsSectionProps, ref: React.Ref<SectionHandle>) {
  const {
    details,
    saveDetails,
    discardChanges,
    isSaving,
    hasUnsavedChanges,
    isLoaded,
    setField,
    setAddressField,
    setBankField,
    setLogoUrl,
  } = useCompanyDetails();
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const { financeStatus } = useDashboardIntegrationStatus();
  const isFinanceConnected = financeStatus.isConnected;
  const financeProductLabel = financeStatus.productLabel ?? 'Access Financials';
  // Details are finance-managed (read-only) when the finance integration is
  // connected or when the parent explicitly forces it (e.g. onboarding).
  const detailsReadOnly = isFinanceConnected || readOnlyDetails;

  // --- Bank account (Financials cash book) selection ---
  const [bankAccounts, setBankAccounts] = useState<CashBookAccountOption[]>(importData?.bankAccounts ?? []);

  const clearBankFields = useCallback(() => {
    setBankField('bankName', '');
    setBankField('accountName', '');
    setBankField('sortCode', '');
    setBankField('accountNumber', '');
    setBankField('iban', '');
    setBankField('swiftBic', '');
    setBankField('cashBookAccountKey', null);
    setBankField('cashBookAccountCode', null);
  }, [setBankField]);

  useEffect(() => {
    if (importData?.bankAccounts) {
      setBankAccounts(importData.bankAccounts);
      return;
    }
    if (isDemoMode || !isFinanceConnected) return;
    let cancelled = false;
    getCashBookAccounts()
      .then((result) => {
        if (!cancelled) setBankAccounts(result.accounts);
      })
      .catch(() => {
        // Bank accounts unavailable — the picker stays hidden
      });
    return () => { cancelled = true; };
  }, [importData, isDemoMode, isFinanceConnected]);

  const selectedAccount = bankAccounts.find((account) =>
    (details.bankDetails?.cashBookAccountKey != null && account.accountKey === details.bankDetails.cashBookAccountKey) ||
    (!!details.bankDetails?.cashBookAccountCode && account.accountCode === details.bankDetails.cashBookAccountCode));

  useEffect(() => {
    onBankAccountSelectionChange?.({
      accountCount: bankAccounts.length,
      hasSelection: selectedAccount != null,
    });
  }, [bankAccounts.length, selectedAccount, onBankAccountSelectionChange]);

  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  // Picking an account persists straight away — the rest of the bank block is
  // finance-owned, so there is nothing else for the user to review before saving.
  const handleBankAccountChange = useCallback(async (value: string) => {
    const account = bankAccounts.find((a) => accountOptionValue(a) === value);
    if (!account) return;
    const bankDetails: BankDetails = {
      ...details.bankDetails,
      bankName: account.bankName,
      accountName: account.bankAccountName,
      sortCode: account.sortCode,
      accountNumber: account.accountNumber,
      iban: account.iban ?? '',
      swiftBic: account.swiftBic ?? '',
      cashBookAccountKey: account.accountKey ?? null,
      cashBookAccountCode: account.accountCode ?? null,
    };
    const previousBankDetails = details.bankDetails;
    setField('bankDetails', bankDetails);
    if (isDemoMode) return;
    try {
      await saveDetails({ ...details, bankDetails });
      appendAuditEntry({
        changedBy,
        category: 'Settings',
        action: 'Updated',
        entityName: 'Company Details',
        newValue: account.label,
        detail: 'Bank account changed via Settings > General > Company Details',
      });
      toast.success('Bank account saved.');
    } catch (err) {
      // Nothing on this tab offers a manual save or discard, so a failed write
      // has to put the picker back on the account that is actually stored.
      setField('bankDetails', previousBankDetails);
      toast.error(err instanceof Error ? err.message : 'Failed to save the bank account.');
    }
  }, [bankAccounts, details, setField, saveDetails, isDemoMode, changedBy]);

  // --- Finance import: pre-populate fields when importData is provided ---
  // Wait for isLoaded so that the local company details fetch has completed
  // before overlaying GraphQL data; otherwise setDetails(loaded) in the hook
  // can overwrite the GraphQL values.
  useEffect(() => {
    if (!importData || !isLoaded) return;
    if (importData.name) setField('name', importData.name);
    if (importData.companyRegistrationNumber) setField('companyRegistrationNumber', importData.companyRegistrationNumber);
    if (importData.vatNumber) setField('vatNumber', importData.vatNumber);
    if (importData.phone) setField('phone', importData.phone);
    if (importData.email) setField('email', importData.email);
    if (importData.website) setField('website', importData.website);
    if (importData.registeredAddress) {
      const a = importData.registeredAddress;
      if (a.line1) setAddressField('line1', a.line1);
      if (a.line2) setAddressField('line2', a.line2 ?? '');
      if (a.city) setAddressField('city', a.city);
      if (a.county) setAddressField('county', a.county ?? '');
      if (a.postcode) setAddressField('postcode', a.postcode);
      if (a.country) setAddressField('country', a.country);
    }
    if (importData.bankAccounts && importData.selectedBankAccountKey == null && !importData.selectedBankAccountCode) {
      // Finance is connected but no cash book account has been chosen yet —
      // the bank details stay blank until an administrator picks one.
      clearBankFields();
    } else if (importData.bankDetails) {
      const b = importData.bankDetails;
      if (b.bankName) setBankField('bankName', b.bankName);
      if (b.accountName) setBankField('accountName', b.accountName);
      if (b.sortCode) setBankField('sortCode', b.sortCode);
      if (b.accountNumber) setBankField('accountNumber', b.accountNumber);
      if (b.iban) setBankField('iban', b.iban);
      if (b.swiftBic) setBankField('swiftBic', b.swiftBic);
      if (importData.selectedBankAccountKey != null) setBankField('cashBookAccountKey', importData.selectedBankAccountKey);
      if (importData.selectedBankAccountCode) setBankField('cashBookAccountCode', importData.selectedBankAccountCode);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importData, isLoaded]);

  // --- Logo drag-and-drop ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleLogoFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, SVG, WebP).');
      return;
    }
    setIsCompressing(true);
    try {
      const compressed = await compressLogoImage(file);
      const savedUrl = await uploadCompanyLogo(compressed);
      setLogoUrl(savedUrl);
      toast.success('Logo saved successfully.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process logo.');
    } finally {
      setIsCompressing(false);
    }
  }, [setLogoUrl]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  }, [handleLogoFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFile(file);
    e.target.value = '';
  }, [handleLogoFile]);

  const handleRemoveLogo = useCallback(async () => {
    try {
      await removeCompanyLogo();
      setLogoUrl(undefined);
      toast.info('Logo removed.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove logo.');
    }
  }, [setLogoUrl]);

  // --- Bubble save/dirty state up to page ---
  const save = useCallback(async () => {
    // Bank details are finance-owned, but the account choice stays editable,
    // so a read-only section still has something to persist.
    if (detailsReadOnly && bankAccounts.length === 0) return;
    await saveDetails();
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'Company Details',
      detail: 'Updated via Settings > General > Company Details',
    });
  }, [saveDetails, changedBy, detailsReadOnly, bankAccounts.length]);

  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onDirtyChange]);

  useImperativeHandle(ref, () => ({ save, discard: discardChanges }), [save, discardChanges]);

  return (
    <AccordionSection
      icon={<Building2 className="h-5 w-5 text-primary" />}
      title="Company Details"
      description="Your company information, address and bank details used on invoices"
    >

      <div className="space-y-8">

        {/* ── Source-of-data notice ────────────────────────────────────── */}
        {detailsReadOnly && !isDemoMode && (
          <Alert variant="info" size="compact">
            <AlertDescription>
              {details.name?.trim()
                ? `Company information, registered address and bank details are synced from the company connected in ${financeProductLabel}, so they can\u2019t be edited here \u2014 only the bank account they come from is chosen in Projects. Update them in ${financeProductLabel} and they\u2019ll refresh once the next sync completes.`
                : `Company information, registered address and bank details are synced from the company connected in ${financeProductLabel}. Nothing has appeared yet \u2014 the sync may still be running, or no company has been selected on the Integrations step. Once a connected company finishes syncing, its details will populate automatically. They can\u2019t be edited here; make any changes in ${financeProductLabel}.`}
            </AlertDescription>
          </Alert>
        )}

        {/* ── Logo Upload ─────────────────────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Logo</h3>
          </div>

          {details.logoUrl ? (
            <div className="flex items-start gap-4">
              <div className="border rounded-lg p-3 bg-muted/20 flex items-center justify-center" style={{ minWidth: 160, minHeight: 60 }}>
                <img
                  src={details.logoUrl}
                  alt="Company logo"
                  className="max-h-[80px] max-w-[300px] object-contain"
                />
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCompressing}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Replace Logo
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              {isCompressing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Processing image…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, SVG, WebP · Max 10 MB · Scaled to 400×120 px</p>
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>

        <Separator />

        {/* ── Company Information ──────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-name" className="flex items-center flex-wrap gap-y-1">
                Company Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cd-name"
                value={details.name}
                onChange={(e) => setField('name', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-reg" className="flex items-center flex-wrap gap-y-1">
                Company Registration Number
              </Label>
              <Input
                id="cd-reg"
                value={details.companyRegistrationNumber ?? ''}
                onChange={(e) => setField('companyRegistrationNumber', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-vat" className="flex items-center flex-wrap gap-y-1">
                VAT Number
              </Label>
              <Input
                id="cd-vat"
                value={details.vatNumber ?? ''}
                onChange={(e) => setField('vatNumber', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-phone" className="flex items-center flex-wrap gap-y-1">
                Phone
              </Label>
              <Input
                id="cd-phone"
                type="tel"
                value={details.phone ?? ''}
                onChange={(e) => setField('phone', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-email" className="flex items-center flex-wrap gap-y-1">
                Email
              </Label>
              <Input
                id="cd-email"
                type="email"
                value={details.email ?? ''}
                onChange={(e) => setField('email', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Registered Address ───────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Registered Address</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr1" className="flex items-center flex-wrap gap-y-1">
                Address Line 1
              </Label>
              <Input
                id="cd-addr1"
                value={details.registeredAddress?.line1 ?? ''}
                onChange={(e) => setAddressField('line1', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr2" className="flex items-center flex-wrap gap-y-1">
                Address Line 2
              </Label>
              <Input
                id="cd-addr2"
                value={details.registeredAddress?.line2 ?? ''}
                onChange={(e) => setAddressField('line2', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-city" className="flex items-center flex-wrap gap-y-1">
                City
              </Label>
              <Input
                id="cd-city"
                value={details.registeredAddress?.city ?? ''}
                onChange={(e) => setAddressField('city', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-county" className="flex items-center flex-wrap gap-y-1">
                County
              </Label>
              <Input
                id="cd-county"
                value={details.registeredAddress?.county ?? ''}
                onChange={(e) => setAddressField('county', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-postcode" className="flex items-center flex-wrap gap-y-1">
                Postcode
              </Label>
              <Input
                id="cd-postcode"
                value={details.registeredAddress?.postcode ?? ''}
                onChange={(e) => setAddressField('postcode', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-country" className="flex items-center flex-wrap gap-y-1">
                Country
              </Label>
              <Input
                id="cd-country"
                value={details.registeredAddress?.country ?? ''}
                onChange={(e) => setAddressField('country', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Bank Details ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Bank Details</h3>
          </div>
          {bankAccounts.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="cd-bankaccount">Bank Account</Label>
              <Select
                value={selectedAccount ? accountOptionValue(selectedAccount) : undefined}
                onValueChange={handleBankAccountChange}
                disabled={isSaving}
              >
                <SelectTrigger id="cd-bankaccount" className="w-full sm:max-w-md">
                  <SelectValue placeholder="Select a bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={accountOptionValue(account)} value={accountOptionValue(account)}>
                      {account.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedAccount
                  ? `The bank details below come from this ${financeProductLabel} cash book account and appear on invoices.`
                  : `Choose which ${financeProductLabel} cash book account supplies the bank details shown on invoices.`}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-bankname" className="flex items-center flex-wrap gap-y-1">
                Bank Name
              </Label>
              <Input
                id="cd-bankname"
                value={details.bankDetails?.bankName ?? ''}
                onChange={(e) => setBankField('bankName', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountname" className="flex items-center flex-wrap gap-y-1">
                Account Name
              </Label>
              <Input
                id="cd-accountname"
                value={details.bankDetails?.accountName ?? ''}
                onChange={(e) => setBankField('accountName', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-sortcode" className="flex items-center flex-wrap gap-y-1">
                Sort Code
              </Label>
              <Input
                id="cd-sortcode"
                value={details.bankDetails?.sortCode ?? ''}
                onChange={(e) => setBankField('sortCode', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountnumber" className="flex items-center flex-wrap gap-y-1">
                Account Number
              </Label>
              <Input
                id="cd-accountnumber"
                value={details.bankDetails?.accountNumber ?? ''}
                onChange={(e) => setBankField('accountNumber', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-iban" className="flex items-center flex-wrap gap-y-1">
                IBAN
              </Label>
              <Input
                id="cd-iban"
                value={details.bankDetails?.iban ?? ''}
                onChange={(e) => setBankField('iban', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-swift" className="flex items-center flex-wrap gap-y-1">
                SWIFT / BIC
              </Label>
              <Input
                id="cd-swift"
                value={details.bankDetails?.swiftBic ?? ''}
                onChange={(e) => setBankField('swiftBic', e.target.value)}
                readOnly={detailsReadOnly}
                disabled={detailsReadOnly}
              />
            </div>
          </div>
        </div>

        {/* Discard link for company details — finance-owned details have nothing to discard */}
        {!detailsReadOnly && hasUnsavedChanges && (
          <div className="flex justify-end pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { discardChanges(); toast.info('Changes discarded.'); }}
              disabled={isSaving}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Discard changes
            </Button>
          </div>
        )}

      </div>
    </AccordionSection>
  );
  }
);
// ============================================================================
// Reporting & Calculations Section
// ============================================================================

const ReportingCalculationsSection = forwardRef<SectionHandle, { onDirtyChange?: (dirty: boolean) => void }>(function ReportingCalculationsSection({ onDirtyChange }, ref) {
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  // EAC settings — local state backed by context (no API until backend adds columns)
  const { eacMethod, setEacMethod, showHoursToComplete, setShowHoursToComplete } = useEacSettings();

  // Administrator-only: hide controls entirely for non-admins (no disabled state)
  const isAdmin = isDemoMode || authenticatedUser?.applicationRole === 'Administrator';

  const {
    settings,
    hasUnsavedChanges,
    isSaving,
    isLoading,
    updateSetting,
    saveSettings,
    resetToDefaults,
  } = useFinanceSettings();

  const save = useCallback(async () => {
    await saveSettings();
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'Reporting & Calculations',
      newValue: `GP Method: ${GP_METHOD_LABELS[settings.gpCalculationMethod]}, Revenue Display: ${REVENUE_DISPLAY_MODES.find((m) => m.value === settings.revenueDisplayMode)?.label ?? settings.revenueDisplayMode}`,
      detail: 'Updated via Settings > General > Reporting & Calculations',
    });
  }, [saveSettings, changedBy, settings]);

  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onDirtyChange]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const handleReset = () => {
    resetToDefaults();
    toast.info('Settings reset to defaults.');
  };

  return (
    <div className="space-y-6">

      {/* ── GP Calculation Method ── */}
      <AccordionSection
        icon={<TrendingUp className="h-5 w-5 text-primary" />}
        title="GP Calculation Method"
        description="Choose how Gross Profit and Gross Margin % are calculated across all Financials tab cards, charts, and tables. This setting applies to every project company-wide."
      >
        <div className="space-y-6">
          <div className={`space-y-3${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>
            {GP_METHODS.map((method) => {
              const isSelected = settings.gpCalculationMethod === method;
              return (
                <div
                  key={method}
                  onClick={() => updateSetting('gpCalculationMethod', method)}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <div
                    className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-muted-foreground/40'
                    }`}
                  >
                    {isSelected && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {GP_METHOD_LABELS[method]}
                      {method === 'simple' && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {GP_METHOD_DESCRIPTIONS[method]}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Separator />
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="gap-1.5 text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>
          </div>
        </div>
      </AccordionSection>

      {/* ── Revenue Display in P&L Report ── */}
      <AccordionSection
        icon={<FileBarChart className="h-5 w-5 text-primary" />}
        title="Revenue Display in P&L Report"
        description="Choose which revenue rows appear in the Project P&L Report. This setting applies company-wide to all P&L Report views."
      >
        <div className="space-y-6">
          <div className={`space-y-3${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>
            {REVENUE_DISPLAY_MODES.map((mode) => {
              const isSelected = settings.revenueDisplayMode === mode.value;
              return (
                <div
                  key={mode.value}
                  onClick={() => updateSetting('revenueDisplayMode', mode.value)}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <div
                    className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-muted-foreground/40'
                    }`}
                  >
                    {isSelected && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {mode.label}
                      {mode.value === 'both' && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{mode.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AccordionSection>

      {/* ── EAC Calculation Method — Administrator only ── */}
      {isAdmin && (
        <AccordionSection
          icon={<Calculator className="h-5 w-5 text-primary" />}
          title="EAC Calculation Method"
          description="Controls how Estimate at Completion (EAC) is calculated across all project forecasts."
        >
          <div className="space-y-6">
            <div className="space-y-3">
              {EAC_METHOD_OPTIONS.map((option) => {
                const isSelected = eacMethod === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => setEacMethod(option.value)}
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <div
                      className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-primary' : 'border-muted-foreground/40'
                      }`}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {option.label}
                        {option.value === 'simple' && (
                          <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Controls how Estimate at Completion (EAC) is calculated across all project forecasts.
            </p>
          </div>
        </AccordionSection>
      )}

      {/* ── Show Hours to Complete — Administrator only ── */}
      {isAdmin && (
        <AccordionSection
          icon={<Timer className="h-5 w-5 text-primary" />}
          title="Show Hours to Complete"
          description="Show a 'Hours remaining' column on timesheet entry to feed EAC calculations."
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div className="space-y-1">
                <p className="text-sm font-medium leading-none">Show Hours to Complete</p>
                <p className="text-xs text-muted-foreground">
                  When enabled, a &apos;Hours remaining&apos; column appears on timesheet entry,
                  allowing employees to record their estimate of remaining hours on each project.
                  This feeds into EAC calculations on the Project Completion Forecast tab.
                </p>
              </div>
              <Switch
                checked={showHoursToComplete}
                onCheckedChange={setShowHoursToComplete}
                aria-label="Show Hours to Complete"
              />
            </div>
          </div>
        </AccordionSection>
      )}

    </div>
  );
});

// ============================================================================
// Dashboard Targets Section
// ============================================================================

const DashboardTargetsSection = forwardRef<SectionHandle, { onDirtyChange?: (dirty: boolean) => void }>(function DashboardTargetsSection({ onDirtyChange }, ref) {
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const {
    settings,
    hasUnsavedChanges: hasFinanceChanges,
    isSaving: isSavingFinance,
    isLoading,
    updateSetting,
    saveSettings,
    resetToDefaults,
  } = useFinanceSettings();

  const {
    formula: utilisationFormula,
    hasUnsavedChanges: hasUnsavedUtilisationChanges,
    isSaving: isSavingUtilisation,
    updateFormula: setUtilisationFormula,
    saveFormula: saveUtilisationFormulaApi,
  } = useUtilisationFormula();

  const utilisationFormulaOptions: { value: UIFormulaType; label: string; description: string }[] = [
    {
      value: 'actual_vs_budget',
      label: 'Actual vs Budgeted Hours',
      description: 'Actual hours ÷ Budgeted hours × 100. The most common formula — measures how much of the hours budget has been consumed.',
    },
    {
      value: 'actual_vs_allocated',
      label: 'Actual vs Allocated Hours',
      description: 'Actual hours ÷ Allocated hours × 100. Measures how much of the allocated (scheduled) capacity has been used.',
    },
    {
      value: 'billable_vs_capacity',
      label: 'Billable Actuals vs Available Capacity',
      description: 'Billable actual hours ÷ Available capacity hours × 100. Used when tracking billable efficiency against total available time.',
    },
  ];

  const hasAnyChanges = hasFinanceChanges || hasUnsavedUtilisationChanges;
  const isAnySaving = isSavingFinance || isSavingUtilisation;

  useEffect(() => { onDirtyChange?.(hasAnyChanges); }, [hasAnyChanges, onDirtyChange]);

  const save = useCallback(async () => {
    const promises: Promise<void>[] = [];
    if (hasFinanceChanges) {
      promises.push(
        saveSettings().then(() => {
          appendAuditEntry({
            changedBy,
            category: 'Settings',
            action: 'Updated',
            entityName: 'Dashboard Targets',
            detail: 'Updated via Settings > General > Dashboard Targets',
          });
        })
      );
    }
    if (hasUnsavedUtilisationChanges) {
      promises.push(
        saveUtilisationFormulaApi().then(() => {
          appendAuditEntry({
            changedBy,
            category: 'Settings',
            action: 'Updated',
            entityName: 'Utilisation Rate Formula',
            detail: 'Updated via Settings > General > Dashboard Targets',
          });
        })
      );
    }
    await Promise.all(promises);
  }, [hasFinanceChanges, hasUnsavedUtilisationChanges, saveSettings, saveUtilisationFormulaApi, changedBy]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const handleResetTargets = () => {
    resetToDefaults();
    toast.info('Dashboard targets reset to defaults.');
  };

  return (
    <div className="space-y-6">

      {/* ── Dashboard Thresholds & Targets ── */}
      <AccordionSection
        icon={<LayoutDashboard className="h-5 w-5 text-primary" />}
        title="Dashboard Reference Lines"
        description="Configure firm-wide benchmark percentages used by Executive Dashboard widgets. Each field is pre-populated with a default — if a field is left blank the default is used, so the benchmark is always calculated."
      >
        <div className="space-y-6">
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>

            {/* Portfolio Target Margin % */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Portfolio Target Margin</p>
                <p className="text-xs text-muted-foreground">
                  Portfolio margin is compared against this target on the Portfolio Profitability widget.
                  Defaults to {DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT}% when blank.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-portfolioMargin"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder={`${DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT}`}
                  value={settings.portfolioTargetMarginPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'portfolioTargetMarginPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.portfolioTargetMarginPercent !== DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('portfolioTargetMarginPercent', DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT)}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
            </div>

            {/* Client Concentration Threshold % */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Client Concentration Threshold</p>
                <p className="text-xs text-muted-foreground">
                  Marks the threshold on the Client Concentration Risk widget and flags a warning when a single
                  client exceeds this share of remaining contract value. Defaults to{' '}
                  {DEFAULT_CONCENTRATION_THRESHOLD_PERCENT}% when blank.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-concentration"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder={`${DEFAULT_CONCENTRATION_THRESHOLD_PERCENT}`}
                  value={settings.concentrationThresholdPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'concentrationThresholdPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.concentrationThresholdPercent !== DEFAULT_CONCENTRATION_THRESHOLD_PERCENT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('concentrationThresholdPercent', DEFAULT_CONCENTRATION_THRESHOLD_PERCENT)}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
            </div>

            {/* Target Utilisation % — left column of second row */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Target Utilisation</p>
                <p className="text-xs text-muted-foreground">
                  Renders a dashed target line on the Utilisation Snapshot gauge and department capacity bars.
                  Defaults to {DEFAULT_TARGET_UTILISATION_PERCENT}% when blank.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-utilisation"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder={`${DEFAULT_TARGET_UTILISATION_PERCENT}`}
                  value={settings.targetUtilisationPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'targetUtilisationPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.targetUtilisationPercent !== DEFAULT_TARGET_UTILISATION_PERCENT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('targetUtilisationPercent', DEFAULT_TARGET_UTILISATION_PERCENT)}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
            </div>

          </div>

          <Separator />
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetTargets}
              className="gap-1.5 text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>
          </div>
        </div>
      </AccordionSection>

      {/* ── Utilisation Rate Formula ── */}
      <AccordionSection
        icon={<BarChart2 className="h-5 w-5 text-primary" />}
        title="Utilisation Rate Formula"
        description="Choose how Utilisation Rate is calculated across all projects and dashboard widgets company-wide."
      >
        <div className="space-y-6">
          <div className="space-y-3">
            {utilisationFormulaOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => setUtilisationFormula(option.value)}
                className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  utilisationFormula === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
                role="radio"
                aria-checked={utilisationFormula === option.value}
              >
                <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                  utilisationFormula === option.value
                    ? 'border-primary'
                    : 'border-muted-foreground/40'
                }`}>
                  {utilisationFormula === option.value && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">{option.label}</p>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AccordionSection>

    </div>
  );
});

// ============================================================================
// Audit Trail Settings Section
// ============================================================================

type AuditRetentionPeriod = 'unlimited' | '1year' | '2years' | '3years' | '5years' | '7years';

interface AuditTrailState {
  enabled: boolean;
  retentionPeriod: AuditRetentionPeriod;
  auditProjectChanges: boolean;
  auditFinancialChanges: boolean;
  auditSettingsChanges: boolean;
  auditUserPermissionChanges: boolean;
  auditBillingInvoicing: boolean;
}

const AUDIT_DEFAULT_STATE: AuditTrailState = {
  enabled: true,
  retentionPeriod: 'unlimited',
  auditProjectChanges: true,
  auditFinancialChanges: true,
  auditSettingsChanges: true,
  auditUserPermissionChanges: true,
  auditBillingInvoicing: true,
};

const RETENTION_OPTIONS: { value: AuditRetentionPeriod; label: string }[] = [
  { value: 'unlimited', label: 'Unlimited (Phase 1 default)' },
  { value: '1year',     label: '1 year' },
  { value: '2years',    label: '2 years' },
  { value: '3years',    label: '3 years' },
  { value: '5years',    label: '5 years' },
  { value: '7years',    label: '7 years' },
];

const AUDIT_CATEGORIES: { key: keyof Omit<AuditTrailState, 'enabled' | 'retentionPeriod'>; label: string; description: string }[] = [
  {
    key: 'auditProjectChanges',
    label: 'Project Changes',
    description: 'Log project create, edit, status change, and delete events.',
  },
  {
    key: 'auditFinancialChanges',
    label: 'Financial Changes',
    description: 'Log budget changes, write-offs, and billing events.',
  },
  {
    key: 'auditSettingsChanges',
    label: 'Settings Changes',
    description: 'Log any change made within Settings.',
  },
  {
    key: 'auditUserPermissionChanges',
    label: 'User & Permission Changes',
    description: 'Log role assignments and user access changes.',
  },
  {
    key: 'auditBillingInvoicing',
    label: 'Billing & Invoicing',
    description: 'Log invoice creation, approval, and write-off events.',
  },
];

const AuditTrailSettingsSection = forwardRef<SectionHandle, { onDirtyChange?: (dirty: boolean) => void }>(function AuditTrailSettingsSection({ onDirtyChange }, ref) {
  const { isDemoMode } = useDataMode();
  const [state, setState] = useState<AuditTrailState>(AUDIT_DEFAULT_STATE);
  const [savedState, setSavedState] = useState<AuditTrailState>(AUDIT_DEFAULT_STATE);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isDemoMode) return;
    apiFetch<{
      auditEnabled: boolean;
      retentionPeriod: string;
      auditProjectChanges: boolean;
      auditFinancialChanges: boolean;
      auditSettingsChanges: boolean;
      auditUserPermissionChanges: boolean;
      auditBillingInvoicing: boolean;
    }>(`${getApiBaseUrl()}/settings/audit`)
      .then((data) => {
        const loaded: AuditTrailState = {
          enabled: data.auditEnabled ?? AUDIT_DEFAULT_STATE.enabled,
          retentionPeriod: (data.retentionPeriod as AuditRetentionPeriod) ?? AUDIT_DEFAULT_STATE.retentionPeriod,
          auditProjectChanges: data.auditProjectChanges ?? AUDIT_DEFAULT_STATE.auditProjectChanges,
          auditFinancialChanges: data.auditFinancialChanges ?? AUDIT_DEFAULT_STATE.auditFinancialChanges,
          auditSettingsChanges: data.auditSettingsChanges ?? AUDIT_DEFAULT_STATE.auditSettingsChanges,
          auditUserPermissionChanges: data.auditUserPermissionChanges ?? AUDIT_DEFAULT_STATE.auditUserPermissionChanges,
          auditBillingInvoicing: data.auditBillingInvoicing ?? AUDIT_DEFAULT_STATE.auditBillingInvoicing,
        };
        setState(loaded);
        setSavedState(loaded);
      })
      .catch(() => {
        // Endpoint unavailable — keep defaults
      });
  }, [isDemoMode]);

  const hasChanges =
    state.enabled !== savedState.enabled ||
    state.retentionPeriod !== savedState.retentionPeriod ||
    AUDIT_CATEGORIES.some((c) => state[c.key] !== savedState[c.key]);

  function updateField<K extends keyof AuditTrailState>(key: K, value: AuditTrailState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  const discard = useCallback(() => {
    setState(savedState);
  }, [savedState]);

  useEffect(() => { onDirtyChange?.(hasChanges); }, [hasChanges, onDirtyChange]);

  const save = useCallback(async () => {
    if (isDemoMode) {
      setSavedState(state);
      return;
    }
    setIsSaving(true);
    try {
      const response = await apiFetch<{
        auditEnabled: boolean;
        retentionPeriod: string;
        auditProjectChanges: boolean;
        auditFinancialChanges: boolean;
        auditSettingsChanges: boolean;
        auditUserPermissionChanges: boolean;
        auditBillingInvoicing: boolean;
      }>(`${getApiBaseUrl()}/settings/audit`, {
        method: 'PUT',
        body: JSON.stringify({
          auditEnabled: state.enabled,
          retentionPeriod: state.retentionPeriod,
          auditProjectChanges: state.auditProjectChanges,
          auditFinancialChanges: state.auditFinancialChanges,
          auditSettingsChanges: state.auditSettingsChanges,
          auditUserPermissionChanges: state.auditUserPermissionChanges,
          auditBillingInvoicing: state.auditBillingInvoicing,
        }),
      });
      const persisted: AuditTrailState = {
        enabled: response.auditEnabled ?? state.enabled,
        retentionPeriod: (response.retentionPeriod as AuditRetentionPeriod) ?? state.retentionPeriod,
        auditProjectChanges: response.auditProjectChanges ?? state.auditProjectChanges,
        auditFinancialChanges: response.auditFinancialChanges ?? state.auditFinancialChanges,
        auditSettingsChanges: response.auditSettingsChanges ?? state.auditSettingsChanges,
        auditUserPermissionChanges: response.auditUserPermissionChanges ?? state.auditUserPermissionChanges,
        auditBillingInvoicing: response.auditBillingInvoicing ?? state.auditBillingInvoicing,
      };
      setState(persisted);
      setSavedState(persisted);
    } finally {
      setIsSaving(false);
    }
  }, [state, isDemoMode]);

  useImperativeHandle(ref, () => ({ save, discard }), [save, discard]);

  const categoriesDisabled = !state.enabled;

  return (
    <div className="space-y-4">
      {/* Merged: Audit Logging + Retention Period */}
      <AccordionSection
        icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        title="Audit Logging"
        description="Enable or disable the master audit log. When off, no events are recorded."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Left card: Enable Audit Logging */}
          <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Enable Audit Logging</p>
                <p className="text-xs text-muted-foreground">
                  Record a tamper-evident log of changes across the system.
                </p>
              </div>
              <Checkbox
                id="audit-master-toggle"
                checked={state.enabled}
                onCheckedChange={(checked) => updateField('enabled', checked === true)}
                className="mt-0.5 shrink-0"
              />
            </div>
          </div>

          {/* Right card: Retention Period */}
          <div className={`rounded-lg border border-border p-4 flex flex-col gap-2${categoriesDisabled ? ' opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm font-semibold">Retention Period</p>
            <p className="text-xs text-muted-foreground">
              How long audit log entries are retained before automatic deletion.
            </p>
            <Select
              value={state.retentionPeriod}
              onValueChange={(val) => updateField('retentionPeriod', val as AuditRetentionPeriod)}
              disabled={categoriesDisabled}
            >
              <SelectTrigger id="audit-retention" className="w-full">
                <SelectValue placeholder="Select retention period" />
              </SelectTrigger>
              <SelectContent>
                {RETENTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!categoriesDisabled && state.retentionPeriod !== 'unlimited' && (
              <p className="text-xs text-muted-foreground">
                Log entries older than the selected period will be automatically purged.
              </p>
            )}
          </div>
        </div>
      </AccordionSection>

      {/* Audit categories */}
      <AccordionSection
        icon={<BarChart2 className="h-5 w-5 text-primary" />}
        title="Audit Categories"
        description="Choose which types of events are recorded in the audit log. Enable audit logging above to configure these."
      >
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4${categoriesDisabled ? ' opacity-50 pointer-events-none' : ''}`}>
          {AUDIT_CATEGORIES.map((category) => (
            <div key={category.key} className="rounded-lg border border-border p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold">{category.label}</p>
                <Checkbox
                  id={`audit-${category.key}`}
                  checked={state[category.key] as boolean}
                  onCheckedChange={(checked) => updateField(category.key, checked === true)}
                  disabled={categoriesDisabled}
                  className="mt-0.5 shrink-0"
                />
              </div>
              <p className="text-xs text-muted-foreground">{category.description}</p>
            </div>
          ))}
        </div>
      </AccordionSection>
    </div>
  );
});

// ============================================================================
// Tab definitions
// ============================================================================

const GENERAL_TABS = [
  {
    value: 'organisation',
    label: 'Organisation',
    icon: Building2,
    description: 'Company information, address and bank details.',
  },
  {
    value: 'reporting',
    label: 'Reporting',
    icon: Calculator,
    description: 'GP calculation method, revenue display, and utilisation formula.',
  },
  {
    value: 'targets',
    label: 'Targets',
    icon: Target,
    description: 'Firm-wide benchmark targets for Executive Dashboard reference lines.',
  },
  {
    value: 'audit',
    label: 'Audit',
    icon: ShieldCheck,
    description: 'Audit log configuration and event retention settings.',
  },
] as const;

type GeneralTab = (typeof GENERAL_TABS)[number]['value'];

// ============================================================================
// Main Page
// ============================================================================

interface SettingsGeneralPageProps {
  defaultTab?: GeneralTab;
}

export function SettingsGeneralPage({ defaultTab }: SettingsGeneralPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSaving, setIsSaving] = useState(false);
  const { canAccessSetting } = usePermissions();
  const { isWizardMode, registerSave } = useWizardMode();
  const { isDemoMode } = useDataMode();
  const { financeStatus: pageFinanceStatus } = useDashboardIntegrationStatus();

  // Fetch company details from Financials GraphQL via the backend endpoint
  const [financeImportData, setFinanceImportData] = useState<CompanyImportData | null>(null);
  useEffect(() => {
    if (isDemoMode || !pageFinanceStatus.isConnected) return;
    apiFetch<CompanyImportData>(`${getApiBaseUrl()}/integrations/finance/company-details`)
      .then((data) => {
        if (data) setFinanceImportData(data);
      })
      .catch(() => {
        // Finance integration connected but endpoint unavailable — leave importData null
      });
  }, [isDemoMode, pageFinanceStatus.isConnected]);

  const canConfigureAudit = canAccessSetting('configureAuditTrail');
  const visibleTabs = canConfigureAudit
    ? GENERAL_TABS
    : GENERAL_TABS.filter((t) => t.value !== 'audit');

  const rawTab = searchParams.get('tab') as GeneralTab | null;
  const validTabs: GeneralTab[] = visibleTabs.map((t) => t.value);
  const activeTab: GeneralTab = defaultTab ?? (rawTab && validTabs.includes(rawTab) ? rawTab : 'organisation');
  // Finance-owned company details have no manual save: the logo and the cash
  // book account both persist as soon as they change.
  const isOrgTabReadOnly = pageFinanceStatus.isConnected && activeTab === 'organisation';

  const handleTabChange = (value: string) => {
    if (defaultTab) return; // tab switching disabled in wizard mode
    setSearchParams({ tab: value }, { replace: true });
  };

  // ── Refs to each section so we can call save() without state bubbling ─────
  const orgRef = useRef<SectionHandle>(null);
  const reportingRef = useRef<SectionHandle>(null);
  const targetsRef = useRef<SectionHandle>(null);
  const auditRef = useRef<SectionHandle>(null);

  // ── Dirty-state tracking from child sections ──────────────────────────────
  const [sectionDirty, setSectionDirty] = useState({ org: false, reporting: false, targets: false, audit: false });
  const hasAnyChanges = Object.values(sectionDirty).some(Boolean);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const setOrgDirty = useCallback((d: boolean) => setSectionDirty(prev => ({ ...prev, org: d })), []);
  const setReportingDirty = useCallback((d: boolean) => setSectionDirty(prev => ({ ...prev, reporting: d })), []);
  const setTargetsDirty = useCallback((d: boolean) => setSectionDirty(prev => ({ ...prev, targets: d })), []);
  const setAuditDirty = useCallback((d: boolean) => setSectionDirty(prev => ({ ...prev, audit: d })), []);

  const handleDiscardAll = useCallback(() => {
    orgRef.current?.discard?.();
    reportingRef.current?.discard?.();
    targetsRef.current?.discard?.();
    auditRef.current?.discard?.();
  }, []);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        orgRef.current?.save(),
        reportingRef.current?.save(),
        targetsRef.current?.save(),
        auditRef.current?.save(),
      ]);
      if (!isWizardMode) {
        toast.success('All settings saved successfully.');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
      }
    } catch {
      toast.error('Some settings failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Register save with wizard so the Next button triggers handleSaveAll.
  const wizardSaveRef = useRef(handleSaveAll);
  wizardSaveRef.current = handleSaveAll;
  useState(() => { if (isWizardMode) registerSave(async () => { await wizardSaveRef.current(); }); });

  const content = (
    <div className="space-y-6 p-6">

      {/* Page header — hidden in wizard (step wrapper provides context) */}
      {!defaultTab && (
        <div>
          <h1 className="text-xl font-semibold">General Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {GENERAL_TABS.find((t) => t.value === activeTab)?.description}
          </p>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        {/* Tab bar — hidden in wizard (single-tab mode) */}
        {!defaultTab && (
          <TabsList className="w-full justify-start">
            {visibleTabs.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex items-center gap-2 flex-none"
              >
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        {/* Organisation tab */}
        <TabsContent value="organisation" className="space-y-4" data-tour="settings-general-company">
          <CompanyDetailsSection ref={orgRef} importData={isWizardMode ? financeImportData : null} onDirtyChange={setOrgDirty} />
        </TabsContent>

        {/* Reporting tab */}
        <TabsContent value="reporting" className="space-y-4" data-tour="settings-general-currency">
          <ReportingCalculationsSection ref={reportingRef} onDirtyChange={setReportingDirty} />
        </TabsContent>

        {/* Targets tab */}
        <TabsContent value="targets" className="space-y-4">
          <DashboardTargetsSection ref={targetsRef} onDirtyChange={setTargetsDirty} />
        </TabsContent>

        {/* Audit tab — gated by configureAuditTrail permission */}
        {canConfigureAudit && (
          <TabsContent value="audit" className="space-y-4">
            <AuditTrailSettingsSection ref={auditRef} onDirtyChange={setAuditDirty} />
          </TabsContent>
        )}

      </Tabs>

      {/* Sticky footer save bar — hidden inside onboarding wizard (the step wrapper saves on Next) */}
      {!defaultTab && !isWizardMode && !isOrgTabReadOnly && (
        <div className="border-t bg-background px-6 py-3 flex items-center justify-end gap-2 shrink-0 mt-4">
          <Button variant="outline" size="sm" onClick={handleDiscardAll} disabled={isSaving || !hasAnyChanges}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSaveAll} disabled={isSaving || !hasAnyChanges}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : showSaveSuccess ? (
              <Check className="h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {showSaveSuccess ? 'Saved' : 'Save'}
          </Button>
        </div>
      )}

    </div>
  );

  if (defaultTab) {
    return content;
  }

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsGeneralPage;
